import React, { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { ArrowLeft, CalendarDays, ClipboardList, Megaphone, AlertTriangle, BarChart3, Database, Users } from 'lucide-react'
import { isAdminUser } from '../utils/admin'

const cards = [
  {
    to: '/admin/matches',
    title: 'Manage Matches',
    description: 'List, create, edit, and delete football fixtures (date, time, teams, league, score).',
    icon: CalendarDays,
  },
  {
    to: '/admin/users',
    title: 'Manage Users',
    description: 'List, create, edit, and delete user accounts (email, name, type, preferences).',
    icon: Users,
  },
  {
    to: '/admin/update-results',
    title: 'Update Results',
    description: 'Enter football, F1, Future Champion, and Top Scorer results.',
    icon: ClipboardList,
  },
  {
    to: '/admin/social-posters',
    title: 'Social Posters',
    description: 'Generate and download News social posters.',
    icon: Megaphone,
  },
  {
    to: '/admin/missing-predictions',
    title: 'Missing Predictions',
    description: 'Rooms and users missing predictions for upcoming matches.',
    icon: AlertTriangle,
  },
  {
    to: '/admin/agent-analytics',
    title: 'Agent Analytics',
    description: 'Room agent usage and analytics.',
    icon: BarChart3,
  },
]

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state: RootState) => state.auth)

  useEffect(() => {
    if (!isAdminUser(user)) navigate('/dashboard', { replace: true })
  }, [user, navigate])

  if (!isAdminUser(user)) return null

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16 sm:pt-20 lg:pt-24 pb-8 sm:pb-12">
        <div className="container mx-auto px-3 xs:px-4 sm:px-6 max-w-5xl">
          <div className="mb-6 flex items-center gap-3">
            <Link to="/dashboard" className="p-2 rounded-xl hover:bg-muted/40 text-muted-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground">Admin</h1>
              <p className="text-sm text-muted-foreground">Signed in as {user?.email}</p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {cards.map(({ to, title, description, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="group rounded-2xl border border-border bg-card/60 p-5 hover:border-primary/40 hover:bg-card transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary group-hover:bg-primary/15">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-foreground mb-1">{title}</h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 pt-8 border-t border-border">
            <div className="mb-4">
              <h2 className="text-lg font-semibold text-foreground">Other database tables</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Academ, Chess, F1, Golf, Restaurant, How I Eat, and platform tables — not BreakRoomCup core
                (users, rooms, matches, predictions).
              </p>
            </div>
            <Link
              to="/admin/db"
              className="group flex items-start gap-3 rounded-2xl border border-border bg-card/40 p-5 hover:border-primary/40 hover:bg-card transition-all max-w-xl"
            >
              <div className="p-2.5 rounded-xl bg-muted/30 text-muted-foreground group-hover:text-primary group-hover:bg-primary/10">
                <Database className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-1">Browse all tables</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  List, create, edit, and delete rows in any non–BreakRoomCup PostgreSQL table.
                </p>
              </div>
            </Link>
          </div>
        </div>
      </main>
    </div>
  )
}

export default AdminDashboard
